import React, { useState, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FilePlus, Download, Upload, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import CpscReview from './CpscReview'; // CHA-267 PR-2 — the shared Review is the hand-entry form
import CpscUploadPreflight from './CpscUploadPreflight';
import { readManualCocRecords, correctedWorkbook } from '../utils/manualCocWorkbook';

/**
 * CpscManualCoc — CHA-209. Lab-agnostic manual CoC entry for non-BV labs: an online form (one report) and a
 * ChainTo Excel template upload. Both hand records to `saveManualCoc`, which builds the SAME draft shape as BV
 * (source:'MANUAL') → review/submit/certify unchanged. Blank template is generated on the fly (no repo binary).
 */
const COLUMNS = [
  'Report No', 'Product ID', 'Style No', 'Color', 'Manufacture Date', 'Test Date', 'HTS Code', 'Citation',
  'Lab Name', 'Lab CPSC ID', 'Lab Location', 'Certificate Type', 'Product Name / Description', 'Test Result',
  'Sample Received Date', 'Notes',
];

export default function CpscManualCoc({ userProfile } = {}) {
  const role = userProfile?.role;
  const isPlatformRole = role === 'platform_owner' || role === 'platform_admin';
  const isAdmin = isPlatformRole || role === 'admin';
  const tenantId = userProfile?.tenantId || 'inmocean';
  const tenantArg = useCallback((extra = {}) => (isPlatformRole ? { tenantId, ...extra } : { ...extra }), [isPlatformRole, tenantId]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  // CHA-544 — the uploaded file waiting on the pre-flight: { fileName, buf, records, rowNumbers, report | null, failed | null }
  const [pending, setPending] = useState(null);
  const [notice, setNotice] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const aiRequest = useRef(0); // the current AI request; a new upload invalidates it (CodeRabbit, #494)

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([COLUMNS]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Manual CoC');
    XLSX.writeFile(wb, 'ChainTo_Manual_CoC_Template.xlsx');
  };

  const submitRecords = useCallback(async (records) => {
    setBusy(true); setError(null); setResult(null);
    try {
      const res = await httpsCallable(getFunctions(), 'saveManualCoc', { timeout: 150000 })(tenantArg({ records })); // CHA-382 / CHA-528 — server's 120s window + 30s transport headroom
      setResult(res.data);
    } catch (e) { setError(e?.message || 'Could not create the draft(s).'); } finally { setBusy(false); }
  }, [tenantArg]);

  // CHA-544 — every upload is pre-flighted first: all findings at once, nothing created until the operator
  // chooses. If the pre-flight itself fails, the file can still be sent as uploaded (never block on it).
  const onFile = async (file) => {
    if (!file) return;
    setBusy(true); setError(null); setResult(null); setNotice(null); setPending(null);
    aiRequest.current += 1; setAiBusy(false);
    try {
      const buf = await file.arrayBuffer();
      const parsed = readManualCocRecords(buf); // CHA-630 — one reader, shared with Amend products
      if (parsed.error) { setError(parsed.error); return; }
      const { records, rowNumbers } = parsed;
      if (!records.length) { setError('No data rows in the template.'); return; }
      const base = { fileName: file.name, buf, records, rowNumbers };
      try {
        const res = await httpsCallable(getFunctions(), 'preflightManualCoc', { timeout: 150000 })(tenantArg({ records, rowNumbers }));
        setPending({ ...base, report: res.data, failed: null });
      } catch (e) {
        setPending({ ...base, report: null, failed: e?.message || 'The pre-flight could not run.' });
      }
    } catch (e) { setError(e?.message || 'Could not read the file.'); } finally { setBusy(false); }
  };

  // CHA-544 (D) — the AI fixer, on the operator's click only. Its proposals join the panel unticked. A failure
  // is shown in the panel and changes nothing else: the deterministic findings stay, and nothing is blocked.
  const askAi = async () => {
    if (!pending) return;
    const { records, rowNumbers, fileName } = pending;
    const id = ++aiRequest.current;
    setAiBusy(true);
    let ai;
    try {
      // The server allows 300s (a large file is several calls, plus one escalation); 30s transport headroom.
      ai = (await httpsCallable(getFunctions(), 'fixManualCocWithAI', { timeout: 330000 })(tenantArg({ records, rowNumbers }))).data;
    } catch (e) {
      ai = { proposals: [], unresolved: [], calls: [], aiError: e?.message || 'The AI fixer could not run.' };
    } finally { if (aiRequest.current === id) setAiBusy(false); }
    setPending((p) => (p && p.fileName === fileName && p.records === records ? { ...p, ai } : p));
  };

  const downloadCorrected = (fixes) => {
    if (!pending) return;
    const out = correctedWorkbook(pending.buf, fixes);
    if (out.error) { setError(out.error); return; }
    const name = String(pending.fileName || 'Manual_CoC.xlsx').replace(/\.xlsx?$/i, '');
    const blob = new Blob([out.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${name}_corrected.xlsx`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
    setNotice(`Downloaded ${a.download} with ${out.applied} change(s)${out.skipped ? ` (${out.skipped} could not be placed and were left out)` : ''}. Upload it to create drafts from the corrected rows.`);
  };

  const createFromUpload = async () => {
    if (!pending) return;
    const { records } = pending;
    setPending(null); setNotice(null);
    await submitRecords(records);
  };

  if (!isAdmin) return <div className="font-plex text-sm text-carbon-gray-70 bg-carbon-gray-10 border border-carbon-border px-4 py-6 text-center">Manual CoC entry is restricted to administrators.</div>;

  return (
    <div className="font-plex text-carbon-ink space-y-4 max-w-4xl">
      <div>
        <h3 className="text-base font-semibold text-carbon-ink">CPSC — Manual CoC (non-BV labs)</h3>
        <p className="text-sm text-carbon-gray-70">Enter a Certificate of Conformity by hand or upload the ChainTo template a lab filled in. Both create the same draft as a BV upload — review, submit, and certify the same way.</p>
      </div>
      {isPlatformRole && <div className="text-xs text-carbon-blue-70 bg-carbon-blue-10 border border-carbon-blue-60 px-3 py-2">Acting as <strong>{role}</strong> against tenant: <strong>{tenantId}</strong></div>}
      {error && <div className="text-sm text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-3 py-2 flex items-start gap-1"><AlertTriangle size={14} className="mt-0.5 shrink-0" /><span>{error}</span></div>}
      {result && (
        <div className="text-sm text-carbon-gray-80 bg-carbon-green-10 border border-carbon-green-50 px-3 py-2 flex items-start gap-1">
          <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-carbon-green-50" />
          <span><strong>{result.created?.length || 0}</strong> draft(s) created{result.rejected?.length ? `, ${result.rejected.length} rejected` : ''}. Review them under CPSC Drafts.{result.rejected?.length ? ` Rejected: ${result.rejected.map((r) => `${r.reportNo} (${r.reason})`).join('; ')}` : ''}</span>
        </div>
      )}

      {notice && <div className="text-sm text-carbon-gray-80 bg-carbon-blue-10 border border-carbon-blue-60 px-3 py-2" role="status">{notice}</div>}
      {pending && pending.report && (
        <CpscUploadPreflight key={pending.fileName + pending.records.length} result={pending.report} fileName={pending.fileName} busy={busy}
          ai={pending.ai || null} aiBusy={aiBusy} onAskAi={askAi}
          onDownload={downloadCorrected} onCreate={createFromUpload} onCancel={() => { setPending(null); setNotice(null); }} />
      )}
      {pending && pending.failed && (
        <div className="text-sm text-carbon-gray-80 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-orange-40 px-3 py-2 space-y-2" data-testid="preflight-failed">
          <div className="flex items-start gap-1"><AlertTriangle size={14} className="mt-0.5 shrink-0 text-carbon-orange-70" /><span>Pre-flight unavailable ({pending.failed}). You can still create drafts from the file as uploaded; problems will be listed as rejections.</span></div>
          <div className="flex gap-2">
            <button type="button" onClick={createFromUpload} disabled={busy} className="px-2 py-1 text-xs border border-carbon-gray-30 hover:bg-carbon-gray-10">Create drafts from the file as uploaded</button>
            <button type="button" onClick={() => setPending(null)} disabled={busy} className="px-2 py-1 text-xs text-carbon-gray-70 hover:text-carbon-ink">Cancel</button>
          </div>
        </div>
      )}

      {/* Excel path */}
      <div className="bg-carbon-white border border-carbon-border p-3 space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-carbon-ink flex items-center gap-1.5"><Upload size={14} /> Upload the ChainTo template</h4>
          <button onClick={downloadTemplate} className="flex items-center gap-1 px-2 py-1 text-xs border border-carbon-gray-30 text-carbon-gray-70 hover:bg-carbon-gray-10"><Download size={13} /> Download blank template</button>
        </div>
        <label className="block border-2 border-dashed border-carbon-gray-30 bg-carbon-gray-10 px-3 py-6 text-center text-sm text-carbon-gray-70 cursor-pointer hover:border-carbon-blue-60">
          {busy ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Working…</span> : 'Drop or click to upload a filled .xlsx template'}
          <input type="file" accept=".xlsx,.xls" className="hidden" disabled={busy} onChange={(e) => onFile(e.target.files?.[0])} />
        </label>
      </div>

      {/* Online form path — CHA-267 PR-2: the hand form IS the shared Review (CERTIFY lane, blank prefill,
          canonical saveManualCoc keys). Ruling 1: the old GCC/SATISFACTORY defaults are GONE — every
          compliance-determining value is a blank forced choice ("never silently GCC"), same as the drop-doc
          review. readRecord accepts canonical keys directly, so the backend is untouched. */}
      <div className="space-y-1">
        <h4 className="text-sm font-semibold text-carbon-ink flex items-center gap-1.5"><FilePlus size={14} /> …or enter one report by hand</h4>
        <CpscReview lane="CERTIFY" prefill={{}} tenantArg={tenantArg} />
      </div>
    </div>
  );
}
